/**
 * Custom Fields Models for Dynamic Observation Forms
 * Represents the structure of custom fields returned by the integration API
 */

export interface CustomFieldOption {
  value: string;
  label: string;
  status: string | null;
  child: CustomField | null;
  /** Optional list of fields that should be auto-populated when this option is selected */
  related_fields?: RelatedFieldRef[];
}

/**
 * A reference to another field that should be auto-populated when a select option is chosen.
 */
export interface RelatedFieldRef {
  /** The target field name in the form (or external binding key) */
  field_name: string;
  /** The value to assign to the target field */
  field_value: any;
  /** Whether the target field should be locked after assignment */
  read_only?: boolean;
}

export type CustomFieldValidationRule =
  | 'required'
  | 'nullable'
  | 'string'
  | 'numeric'
  | 'date'
  | `min:${number}`
  | `max:${number}`
  | `in:${string}`
  | 'date_format:Y-m-d H:i:s';

/**
 * Field dependency definition
 * If provided, this field will only be visible and validated when
 * the referenced parent field's value matches one of the allowed values.
 */
export interface FieldDependency {
  field: string; // parent field name
  value: string | string[]; // allowed values that activate this field
}

export interface CustomField {
  name: string;
  label: string;
  type: CustomFieldType;
  required: boolean;
  options: CustomFieldOption[];
  placeholder: string;
  empty_message?: string | null;
  default: any;
  read_only: boolean;
  auto_select: string;
  unifier_field_name?: string;
  depends_on: FieldDependency | null;
  field_validation?: {
    [key: string]: any;
  };
  validations: {
    [key: string]: CustomFieldValidationRule[];
  };
}

export interface CustomFieldWithValue extends CustomField {
  currentValue?: any;
}

export type CustomFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'datetime'
  | 'select'
  | 'boolean';

export interface CustomFieldsResponse {
  status: 'success' | 'error';
  message: string;
  error_code: string | null;
  meta_data: any;
  data: CustomField[];
  errors: any;
}

export interface CustomFieldValue {
  [fieldName: string]: any;
}

/**
 * Configuration for field rendering
 */
export interface FieldRenderConfig {
  field: CustomField;
  value?: any;
  disabled?: boolean;
  formControlName?: string;
}

/**
 * Form Group with metadata for custom field dependency resolution
 * Extends UntypedFormGroup to include metadata about categories and other context
 */
export interface CustomFieldsFormGroup {
  metadata?: {
    categories?: Array<{
      label: string;
      value: string | number;
      children?: any[];
    }>;
  };
}
